import React, { useMemo, useState, useEffect } from 'react'
import { signedPoints, deltaClass } from '../lib/format'
import { useIsCompact } from '../lib/useMediaQuery'

// Small multiples: one diverging row per roster position, one bar per week.
//
// Form follows the data's job — signed change against a zero baseline — so bars
// read up for a gain and down for a loss. Sign is carried by direction as well
// as color, which means the encoding survives colorblindness and grayscale
// printing on its own.
//
// Every row shares one scale. Per-row scaling would make a 0.3 point wobble at
// TE look identical to a 9 point swing at RB, which is exactly the comparison
// this chart exists to support.

// Most-used slots first; anything unrecognized sorts to the end alphabetically.
const SLOT_ORDER = [
  'QB', 'RB', 'WR', 'TE', 'FLEX', 'WRRB_FLEX', 'REC_FLEX', 'SUPER_FLEX', 'K', 'DEF',
]

function slotRank(slot) {
  const index = SLOT_ORDER.indexOf(slot)
  return index === -1 ? SLOT_ORDER.length : index
}

// Fourteen weeks in ~183px of usable phone width is about 11px per column,
// which collapses the bars and collides the axis labels. Show a window instead.
const COMPACT_WINDOW = 6

export default function PositionWeekGrid({ weeks, teamId, averages, selectedWeek, onSelectWeek }) {
  const [hovered, setHovered] = useState(null)
  const compact = useIsCompact()
  const [windowStart, setWindowStart] = useState(0)

  const rows = useMemo(() => {
    const slots = new Set()
    weeks.forEach((week) => {
      const byPosition = week.teams?.[teamId]?.delta?.points_by_position || {}
      Object.keys(byPosition).forEach((slot) => slots.add(slot))
    })
    Object.keys(averages || {}).forEach((slot) => slots.add(slot))

    return [...slots]
      .sort((a, b) => slotRank(a) - slotRank(b) || a.localeCompare(b))
      .map((slot) => ({
        slot,
        average: averages?.[slot] ?? 0,
        cells: weeks.map((week) => ({
          week: week.week,
          value: week.teams?.[teamId]?.delta?.points_by_position?.[slot] ?? 0,
        })),
      }))
  }, [weeks, teamId, averages])

  // Deliberately computed across EVERY week, not the visible window. Scaling to
  // the window would resize the bars as you page, so a quiet stretch would look
  // identical to a decisive one — the exact misreading a shared scale prevents.
  const magnitude = useMemo(
    () => Math.max(...rows.flatMap((row) => row.cells.map((c) => Math.abs(c.value))), 0.1),
    [rows]
  )

  const windowSize = compact ? Math.min(COMPACT_WINDOW, weeks.length) : weeks.length
  const maxStart = Math.max(0, weeks.length - windowSize)
  const start = Math.min(windowStart, maxStart)

  // Picking a week elsewhere (the scrubber, or the chart itself) has to bring it
  // into view, or the selection would be invisible.
  useEffect(() => {
    if (!compact || selectedWeek == null) return

    const index = weeks.findIndex((week) => week.week === selectedWeek)
    if (index === -1) return

    setWindowStart((current) => {
      const limit = Math.max(0, weeks.length - windowSize)
      const clamped = Math.min(current, limit)

      if (index < clamped) return index
      if (index >= clamped + windowSize) return Math.min(index - windowSize + 1, limit)
      return clamped
    })
  }, [compact, selectedWeek, weeks, windowSize])

  const visibleRows = useMemo(
    () => rows.map((row) => ({ ...row, cells: row.cells.slice(start, start + windowSize) })),
    [rows, start, windowSize]
  )

  if (rows.length === 0) return null

  const windowed = compact && weeks.length > windowSize
  const firstWeek = visibleRows[0]?.cells[0]?.week
  const lastWeek = visibleRows[0]?.cells.at(-1)?.week

  return (
    <div>
      {/* Readout doubles as the legend. Putting the hovered value here rather
          than in a floating tooltip keeps it from being clipped by the card. */}
      <div className="mb-3 flex h-5 items-center justify-between text-xs">
        {hovered ? (
          <span className="font-medium text-slate-200">
            Week {hovered.week} · {hovered.slot}{' '}
            <span className={`tabular-nums ${deltaClass(hovered.value)}`}>
              {signedPoints(hovered.value)}
            </span>
          </span>
        ) : (
          <span className="flex items-center gap-3 text-slate-400">
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-[1px]"
                style={{ background: 'var(--gain)' }}
                aria-hidden="true"
              />
              gained
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-[1px]"
                style={{ background: 'var(--loss)' }}
                aria-hidden="true"
              />
              lost
            </span>
          </span>
        )}
        <span className="tabular-nums text-slate-500">
          scale ±{magnitude.toFixed(1)}
        </span>
      </div>

      <div className="mb-1 flex items-end gap-2 sm:gap-3">
        <span className="w-10 shrink-0 sm:w-12" />
        <span className="w-11 shrink-0 text-right text-[10px] uppercase tracking-wide sm:w-14 text-slate-500">
          avg
        </span>

        {windowed ? (
          <span className="flex flex-1 items-center justify-between">
            <button
              type="button"
              onClick={() => setWindowStart(Math.max(0, start - windowSize))}
              disabled={start === 0}
              aria-label="Earlier weeks"
              className="rounded px-2 py-0.5 text-sm disabled:opacity-30 text-slate-400"
            >
              ‹
            </button>
            <span className="text-[10px] uppercase tracking-wide text-slate-500">
              weeks {firstWeek}–{lastWeek}
            </span>
            <button
              type="button"
              onClick={() => setWindowStart(Math.min(maxStart, start + windowSize))}
              disabled={start >= maxStart}
              aria-label="Later weeks"
              className="rounded px-2 py-0.5 text-sm disabled:opacity-30 text-slate-400"
            >
              ›
            </button>
          </span>
        ) : (
          <span className="flex-1" />
        )}
      </div>

      <div className="space-y-2.5">
        {visibleRows.map((row) => (
          <div key={row.slot} className="flex items-center gap-2 sm:gap-3">
            <span className="w-10 shrink-0 text-xs font-medium sm:w-12 text-slate-300">
              {row.slot}
            </span>

            <span
              className={`w-11 shrink-0 text-right text-xs tabular-nums sm:w-14 ${deltaClass(row.average)}`}
              title={`Season average for ${row.slot}`}
            >
              {signedPoints(row.average)}
            </span>

            <div className="relative flex h-20 flex-1 gap-0.5 sm:h-24">
              <span
                className="pointer-events-none absolute inset-x-0 top-1/2 h-px"
                style={{ background: 'var(--diverging-axis)' }}
                aria-hidden="true"
              />

              {row.cells.map((cell) => {
                const gain = cell.value >= 0
                const height = `${(Math.abs(cell.value) / magnitude) * 50}%`
                const selected = cell.week === selectedWeek

                return (
                  <button
                    key={cell.week}
                    type="button"
                    onClick={() => onSelectWeek?.(cell.week)}
                    onMouseEnter={() => setHovered({ ...cell, slot: row.slot })}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered({ ...cell, slot: row.slot })}
                    onBlur={() => setHovered(null)}
                    title={`Week ${cell.week} · ${row.slot} · ${signedPoints(cell.value)} pts`}
                    aria-label={`Week ${cell.week}, ${row.slot}, ${signedPoints(cell.value)} points`}
                    className="group relative min-w-0 flex-1 rounded-sm transition-colors hover:bg-white/[0.05]"
                  >
                    {/* Selection is a crosshair guide, not a filled column. A
                        filled block reads as missing data — especially on a row
                        that is legitimately all zeros. */}
                    {selected && (
                      <span
                        className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-slate-600"
                        aria-hidden="true"
                      />
                    )}

                    {/* Capped width and centered so a bar reads as a bar. At
                        full column width these become horizontal dashes, which
                        is unreadable as magnitude. */}
                    <span
                      className={`absolute left-1/2 w-[76%] max-w-[26px] -translate-x-1/2 ${
                        gain ? 'rounded-t-[3px]' : 'rounded-b-[3px]'
                      }`}
                      style={{
                        background: gain ? 'var(--gain)' : 'var(--loss)',
                        height,
                        ...(gain ? { bottom: '50%' } : { top: '50%' }),
                      }}
                      aria-hidden="true"
                    />
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Week axis, labelled sparsely so the numbers never collide. */}
      <div className="mt-1.5 flex items-center gap-2 sm:gap-3">
        <span className="w-10 shrink-0 sm:w-12" />
        <span className="w-11 shrink-0 sm:w-14" />
        <div className="flex flex-1 gap-0.5">
          {(visibleRows[0]?.cells || []).map((cell, index, all) => {
            const selected = cell.week === selectedWeek
            // A short window has room to label everything; a full season does not.
            const labelled =
              windowed ||
              selected ||
              index === 0 ||
              index === all.length - 1 ||
              cell.week % 5 === 0

            return (
              <span
                key={cell.week}
                className={`min-w-0 flex-1 text-center text-[10px] tabular-nums ${
                  selected
                    ? 'font-semibold text-slate-200'
                    : 'text-slate-500'
                }`}
              >
                {labelled ? cell.week : ''}
              </span>
            )
          })}
        </div>
      </div>
    </div>
  )
}
